import { useNavigate } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import CTABanner from "../components/CTABanner";
import { IconCheck, IconCocktail, IconArrow } from "../components/icons";
import { PACKAGES, SPIRIT_EXPERIENCES } from "../data";
import heroPackages from "../assets/images/hero/hero-packages.png";
import bespokeImg from "../assets/images/your-experience/bespoke.JPG";

export default function Experience() {
  const navigate = useNavigate();
  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* ── Page Hero ── */}
      <section className="sb-noise" style={{
        position: "relative", overflow: "hidden",
        backgroundImage: `url(${heroPackages})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "clamp(60px, 10vw, 100px) 0 clamp(48px, 8vw, 80px)", textAlign: "center",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(58,24,68,0.75), rgba(80,30,60,0.68))",
          zIndex: 0,
        }} />
        <div style={{ position: "absolute", top: "-30%", right: "-10%", width: "400px", height: "400px", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,168,83,0.08), transparent 70%)", pointerEvents: "none", zIndex: 1 }} />
        <div className="sb-container" style={{ position: "relative", zIndex: 2 }}>
          <Reveal>
            <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: "12px" }}>
              Your Experience & Packages
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 style={{ color: "var(--white)", maxWidth: "700px", margin: "0 auto 16px" }}>
              Choose Your <span style={{ color: "var(--gold)" }}>Perfect</span> Cocktail Experience
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p style={{ color: "rgba(255,255,255,0.65)", maxWidth: "560px", margin: "0 auto", fontSize: "1.05rem" }}>
              From hands-on cocktail making to fully served experiences — discover the package that suits your celebration, your guests, and your style.
            </p>
          </Reveal>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--blush-tint), transparent)" }} />
      </section>

      {/* ── Choose Your Experience ── */}
      <section className="sb-section">
        <div className="sb-container">
          <SectionHeader label="Step 1" title="Choose Your Experience" subtitle="Choose the one that fits your event best." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", gap: "28px" }}>
            {[
              {
                icon: "🍸", title: "Cocktail Making Class", tag: "Interactive & Hands-On",
                desc: "Shake, stir, and sip their way through a fun and inclusive two hour cocktail making class. Learn to craft iconic cocktails using professional equipment, ingredients and fresh garnishes. Expect an interactive experience filled with laughter, light hearted games, and expert tips, bringing everyone together to create unforgettable moments.",
                bullets: ["Step-by-step guidance from our mixologist", "Each guest makes their own cocktails", "Fun games and competition", "All ingredients, equipment & glassware included", "Set up and clear down included"],
              },
              {
                icon: "🥂", title: "Cocktail Service", tag: "Sit Back & Sip",
                desc: "Our bartender takes care of crafting and serving cocktails throughout your event, so you and your guests can simply enjoy the moment. You choose the cocktails, and we handle the rest. Our two hour service includes a curated selection of four cocktails per guest. Perfect for those who want to turn up, raise a glass, and celebrate with ease.",
                bullets: ["Professional bartender at your service", "We make and serve your cocktails", "Sip and socialise", "Glassware, garnishes & equipment included", "Set up and clear down included"],
              },
            ].map((exp, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-lg)",
                  padding: "clamp(28px, 4vw, 40px)", boxShadow: "var(--shadow-md)",
                  border: "1px solid rgba(212,168,83,0.08)", height: "100%",
                  display: "flex", flexDirection: "column",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
                    <span style={{ fontSize: "2rem" }}>{exp.icon}</span>
                    <span style={{
                      fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.72rem",
                      letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--rose)",
                      padding: "4px 12px", background: "var(--rose-light)", borderRadius: "99px",
                    }}>{exp.tag}</span>
                  </div>
                  <h3 style={{ marginBottom: "12px", color: "var(--plum)" }}>{exp.title}</h3>
                  <p style={{ color: "var(--text-secondary)", marginBottom: "20px", lineHeight: 1.7 }}>{exp.desc}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
                    {exp.bullets.map((b, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "0.9rem", color: "var(--text-primary)" }}>
                        <span style={{ color: "var(--gold)", marginTop: "2px", flexShrink: 0 }}><IconCheck /></span> {b}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Packages ── */}
      <section className="sb-section" style={{ background: "var(--blush-glow)" }}>
        <div className="sb-container">
          <SectionHeader
            label="Step 2"
            title="Pick Your Package"
            subtitle="Each package includes expert instruction, all ingredients and equipment, and a cocktail menu designed to impress."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "24px" }}>
            {PACKAGES.map((pkg, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-lg)",
                  overflow: "hidden", boxShadow: "var(--shadow-md)",
                  border: "1px solid rgba(212,168,83,0.08)",
                  display: "flex", flexDirection: "column", height: "100%",
                  transition: "transform 0.35s ease, box-shadow 0.35s ease",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
                >
                  <div style={{
                    background: `linear-gradient(135deg, var(--plum), ${pkg.accent}40)`,
                    padding: "28px 28px 24px", position: "relative",
                  }}>
                    <span style={{
                      display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
                      fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase",
                      color: pkg.accent, padding: "4px 12px",
                      background: `${pkg.accent}22`, borderRadius: "99px", marginBottom: "10px",
                    }}>{pkg.tag}</span>
                    <h3 style={{ color: "var(--white)", marginBottom: "8px" }}>{pkg.name}</h3>
                    <p style={{ fontFamily: "var(--font-body)", fontSize: "0.9rem", color: "rgba(255,255,255,0.75)", fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
                      {pkg.tagline}
                    </p>
                  </div>

                  <div style={{ padding: "24px 28px 28px", flex: 1, display: "flex", flexDirection: "column" }}>
                    <div style={{ marginBottom: "20px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.8rem", color: "var(--text-light)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "10px", display: "block" }}>
                        Cocktail Menu
                      </span>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {pkg.cocktails.map((c, j) => (
                          <div key={j} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.92rem", color: "var(--text-primary)" }}>
                            <span style={{ color: pkg.accent }}><IconCocktail /></span> {c}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                      {pkg.features.map((f, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "0.88rem", color: "var(--text-secondary)" }}>
                          <span style={{ color: "var(--gold)", marginTop: "2px", flexShrink: 0 }}><IconCheck /></span> {f}
                        </div>
                      ))}
                    </div>

                    <button
                      className="sb-btn sb-btn-dark"
                      onClick={() => { navigate(`/quote?package=${pkg.slug}`); window.scrollTo({ top: 0 }); }}
                      style={{ width: "100%", justifyContent: "center", marginTop: "24px" }}
                    >
                      Enquire About This Package <IconArrow />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Spirit Experiences ── */}
      <section className="sb-section">
        <div className="sb-container">
          <SectionHeader
            label="Specialist Experiences"
            title="With a Spirit of Your Choice"
            subtitle="Want to focus on a single spirit? Our themed experiences let your guests explore one flavour world in depth."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "24px" }}>
            {SPIRIT_EXPERIENCES.map((sp, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-md)", padding: "28px",
                  boxShadow: "var(--shadow-sm)", border: "1px solid rgba(212,168,83,0.06)",
                  textAlign: "center", transition: "transform 0.3s, box-shadow 0.3s",
                  display: "flex", flexDirection: "column",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
                >
                  <span style={{ fontSize: "2.4rem", display: "block", marginBottom: "14px" }}>{sp.icon}</span>
                  <h3 style={{ marginBottom: "8px", color: "var(--plum)" }}>The {sp.spirit} Experience</h3>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", marginBottom: "20px", flex: 1 }}>{sp.desc}</p>
                  <button
                    className="sb-btn sb-btn-dark"
                    onClick={() => { navigate(`/quote?package=specialist&spirit=${sp.spirit.toLowerCase()}`); window.scrollTo({ top: 0 }); }}
                    style={{ width: "100%", justifyContent: "center" }}
                  >
                    Enquire <IconArrow />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bespoke ── */}
      <section className="sb-section" style={{ background: "var(--blush-glow)" }}>
        <div className="sb-container">
          <div style={{
            background: "linear-gradient(135deg, var(--plum) 0%, var(--plum-mid) 100%)",
            borderRadius: "var(--radius-xl)", padding: "clamp(36px, 6vw, 60px)",
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: "40px",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, right: 0, width: "300px", height: "300px", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,168,83,0.1), transparent 70%)", pointerEvents: "none" }} />
            <div style={{ flex: "1 1 400px", position: "relative", zIndex: 2 }}>
              <Reveal>
                <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: "12px" }}>
                  Something Unique?
                </span>
                <h2 style={{ color: "var(--white)", marginBottom: "14px" }}>
                  Bespoke Packages, <span style={{ color: "var(--gold)" }}>Built Around You</span>
                </h2>
                <p style={{ color: "rgba(255,255,255,0.65)", marginBottom: "24px", lineHeight: 1.75, maxWidth: "500px" }}>
                  Got a theme? A favourite spirit? A huge guest list? No problem. We create fully customised cocktail experiences tailored to your vision, your venue, and your vibe. From menu design to event coordination — we'll make it happen.
                </p>
                <button className="sb-btn sb-btn-primary" onClick={() => { navigate("/quote?package=bespoke"); window.scrollTo({ top: 0 }); }}>
                  Request a Bespoke Quote <IconArrow />
                </button>
              </Reveal>
            </div>
            <div style={{ flex: "1 1 280px" }}>
              <img
                src={bespokeImg}
                alt="Bespoke cocktail event"
                loading="lazy"
                style={{
                  width: "100%", aspectRatio: "4/3", objectFit: "cover",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <CTABanner headline="Let's Plan Your Perfect Event" sub="Tell us about your celebration and we'll recommend the ideal package." btnText="Get in Touch" />
    </main>
  );
}
